import type { ReactNode } from 'react';
import type { Settings } from '@il-colpo/shared/settings';
import styles from './puzzles.module.css';

/**
 * The vehicles of the escape puzzle, seen from above and drawn from code: the getaway car in the style
 * the player wears (a cosmetic, GDD §15), the police cars and the trucks that block the way. They are
 * drawn on a plain block of the board (the rectangle keeps its colours and its selection) and are
 * not there to be clicked. A vehicle is drawn pointing right, and turned a quarter for a vertical one.
 */

export type CarStyle = Settings['game']['escapeCar'];
/** The getaway car, a police car, a truck (three cells long), or any other car. */
export type VehicleKind = 'car' | 'police' | 'truck' | 'van';

/** The size of a cell of the board, in the units of the board's own drawing. */
export const CELL = 48;

const L = (len: number, fraction: number): number => len * CELL * fraction;

function Wheels({ len }: { len: number }) {
  const axles = len >= 3 ? [0.12, 0.74] : [0.15, 0.68];
  return (
    <>
      {axles.flatMap((a) => [
        <rect
          key={`t${String(a)}`}
          className={styles['wheel']}
          x={L(len, a)}
          y={0.5}
          width={14}
          height={5}
          rx={2}
        />,
        <rect
          key={`b${String(a)}`}
          className={styles['wheel']}
          x={L(len, a)}
          y={CELL - 5.5}
          width={14}
          height={5}
          rx={2}
        />,
      ])}
    </>
  );
}

function Lamps({ len }: { len: number }) {
  const x = len * CELL;
  return (
    <>
      <circle className={styles['lamp']} cx={x - 7} cy={12} r={2.4} />
      <circle className={styles['lamp']} cx={x - 7} cy={CELL - 12} r={2.4} />
      <rect className={styles['tail']} x={4.5} y={9} width={3} height={6} rx={1} />
      <rect className={styles['tail']} x={4.5} y={CELL - 15} width={3} height={6} rx={1} />
    </>
  );
}

/** The glass of a car: a windscreen in front and a rear window behind, the roof between them. */
function Cabin({ len, style }: { len: number; style: CarStyle | 'plain' }) {
  const w = len * CELL;
  const open = style === 'roadster';
  const front = style === 'coupe' ? 0.52 : 0.58;
  const rear = style === 'coupe' ? 0.36 : 0.34;
  return (
    <>
      <polygon
        className={styles['glass']}
        points={`${String(w * front)},12 ${String(w * (front + 0.1))},16 ${String(w * (front + 0.1))},${String(CELL - 16)} ${String(w * front)},${String(CELL - 12)}`}
      />
      {open ? (
        <>
          <rect className={styles['seat']} x={w * 0.24} y={12} width={w * 0.12} height={9} rx={3} />
          <rect
            className={styles['seat']}
            x={w * 0.24}
            y={CELL - 21}
            width={w * 0.12}
            height={9}
            rx={3}
          />
        </>
      ) : (
        <polygon
          className={styles['glass']}
          points={`${String(w * rear)},12 ${String(w * (rear - 0.1))},16 ${String(w * (rear - 0.1))},${String(CELL - 16)} ${String(w * rear)},${String(CELL - 12)}`}
        />
      )}
    </>
  );
}

function Pieces({ len, kind, style }: { len: number; kind: VehicleKind; style: CarStyle }) {
  const w = len * CELL;
  const roof = (x: number, node: ReactNode) => (
    <g transform={`translate(${String(x)} 0)`}>{node}</g>
  );
  if (kind === 'truck') {
    // A cab at the front, and a box behind it.
    return (
      <>
        <Wheels len={len} />
        <rect
          className={styles['cargo']}
          x={w * 0.08}
          y={9}
          width={w * 0.56}
          height={CELL - 18}
          rx={3}
        />
        <polygon
          className={styles['glass']}
          points={`${String(w * 0.7)},12 ${String(w * 0.78)},16 ${String(w * 0.78)},${String(CELL - 16)} ${String(w * 0.7)},${String(CELL - 12)}`}
        />
        <Lamps len={len} />
      </>
    );
  }
  return (
    <>
      <Wheels len={len} />
      <Cabin len={len} style={kind === 'car' ? style : 'plain'} />
      <Lamps len={len} />
      {kind === 'police'
        ? roof(
            w * 0.4,
            <>
              <rect className={styles['sirenA']} x={0} y={14} width={6} height={20} rx={2} />
              <rect className={styles['sirenB']} x={6} y={14} width={6} height={20} rx={2} />
            </>,
          )
        : null}
      {kind === 'car' && style === 'taxi'
        ? roof(
            w * 0.43,
            <rect className={styles['sign']} x={0} y={18} width={10} height={12} rx={2} />,
          )
        : null}
      {kind === 'car' && style === 'roadster' ? (
        <>
          <path
            className={styles['stripe']}
            d={`M${String(w * 0.06)} ${String(CELL / 2 - 3)}H${String(w * 0.9)}`}
          />
          <path
            className={styles['stripe']}
            d={`M${String(w * 0.06)} ${String(CELL / 2 + 3)}H${String(w * 0.9)}`}
          />
        </>
      ) : null}
      {kind === 'car' && style === 'coupe' ? (
        <>
          <rect className={styles['mirror']} x={w * 0.5} y={0.5} width={5} height={3} rx={1} />
          <rect
            className={styles['mirror']}
            x={w * 0.5}
            y={CELL - 3.5}
            width={5}
            height={3}
            rx={1}
          />
        </>
      ) : null}
    </>
  );
}

/**
 * The decoration of one block of the board. `horiz` says how the block lies: a vertical one is the
 * same drawing turned a quarter, so that the front is always where the block moves forward.
 */
export function VehicleArt({
  len,
  horiz,
  kind,
  style,
}: {
  len: number;
  horiz: boolean;
  kind: VehicleKind;
  style: CarStyle;
}) {
  return (
    <g
      className={styles['vehicle']}
      transform={horiz ? undefined : `translate(${String(CELL)} 0) rotate(90)`}
      pointerEvents="none"
      aria-hidden="true"
    >
      <Pieces len={len} kind={kind} style={style} />
    </g>
  );
}

/** A getaway car on its own, for the collection: the board's block and the style, in a box of its own. */
export function CarPicture({ style, size = '6rem' }: { style: CarStyle; size?: string }) {
  return (
    <svg
      viewBox={`0 0 ${String(2 * CELL)} ${String(CELL)}`}
      width={size}
      className={`${styles['block']} ${styles['car']}`}
      data-style={style}
      aria-hidden="true"
      focusable="false"
    >
      <rect x={3} y={3} width={2 * CELL - 6} height={CELL - 6} rx={8} />
      <VehicleArt len={2} horiz kind="car" style={style} />
    </svg>
  );
}
